import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./DataTable.module.css";
import DataTableColumn from "./DataTableColumn";

/**
 * DataTable
 * @type React.FC<DataTablePropTypes>
 */
const DataTable = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(DataTablePropTypes).includes(p)) {
      console.error(`DataTable: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  const { dataSource, columns, mergeColumns } = props;

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 가져오기 */
    getRows() {
      return _getRows();
    },
    setDataBinding(source) {
      _setDataBinding(source);
    },
  }));

  const refDataTableColumn = useRef();

  /**
   * DataTable Component의 State
   */
  const [currState, setCurrState] = useState({
    dataSource: dataSource,
    columns: columns,
    mergeColumns: mergeColumns,
    transData: null,
  });

  useEffect(() => {
    const transData = _calculationFunctions(currState.dataSource);

    setCurrState((prev) => ({
      ...prev,
      transData: transData,
    }));
  }, [currState.dataSource]);

  const _calculationFunctions = (data) => {
    if (data?.length) {
      // Helper function to calculate rowSpan for a single field
      const calculateRowSpans = (data, field) => {
        const result = [];
        let spanCount = 1;

        for (let i = 0; i < data.length; i++) {
          if (i > 0 && data[i][field] === data[i - 1][field]) {
            spanCount++;
            result[i] = { ...data[i], [`${field}_rowSpan`]: null };
          } else {
            spanCount = 1;
            result[i] = { ...data[i], [`${field}_rowSpan`]: spanCount };
          }
        }

        // Correct rowSpan for the starting row of the same value
        for (let i = 0; i < result.length; i++) {
          if (result[i][`${field}_rowSpan`] === null && i > 0) {
            let j = i;
            while (j > 0 && result[j - 1][field] === result[i][field]) {
              j--;
            }
            result[j][`${field}_rowSpan`] = i - j + 1;
          }
        }

        return result;
      };

      // Calculate rowSpan for each field
      const calculateAllRowSpans = (data, fields) => {
        let result = [...data];

        // Apply rowSpan calculations for each field in spanFields
        fields?.forEach((field) => {
          result = calculateRowSpans(result, field);
        });

        // Set rowSpan to 1 for all non-spanFields fields
        const spanFieldsWithRowSpan = new Set(fields?.map((field) => `${field}_rowSpan`));
        result = result.map((item) => {
          const newItem = { ...item };
          for (const key of Object.keys(item)) {
            // Only set rowSpan for fields not in spanFields
            if (!spanFieldsWithRowSpan.has(`${key}_rowSpan`) && !key.endsWith("_rowSpan")) {
              newItem[`${key}_rowSpan`] = 1;
            }
          }
          return newItem;
        });

        return result;
      };

      const spanFields = currState.mergeColumns;
      return calculateAllRowSpans(data, spanFields);
    }
  };

  const _getRows = () => {
    const rowsData = refDataTableColumn.current.getRows();
    setCurrState((prev) => ({
      ...prev,
      dataSource: rowsData,
    }));

    return rowsData;
  };

  const _setDataBinding = (source) => {
    setCurrState((prev) => ({ ...prev, dataSource: source }));
  };

  // create header
  const _createHeader = () => {
    const headerRows = _checkHeaderRowSpan(columns);
    headerRows?.forEach((data, index) => {
      if (!data.length) return;
      if (headerRows.length === index + 1) return;

      data?.forEach((item) => {
        item.colSpan = _checkHeaderColSpan(item, 1);
        item.rowSpan = _childrenRowCount(item, headerRows.length);
      });
    });

    return headerRows?.map((row, index) => {
      const rowThKey = `DataTable+th_${index}_${Math.random()}`;
      return (
        <tr key={`${rowThKey}`}>
          {row?.map((item, idx) => {
            const rowTrKey = `DataTable+th_${idx}_${Math.random()}`;
            return (
              <th key={`${rowTrKey}`} rowSpan={item.rowSpan} colSpan={item.colSpan}>
                {item.header}
              </th>
            );
          })}
        </tr>
      );
    });
  };

  const _checkHeaderColSpan = (data) => {
    // when child node does not exists, colSpan is 1
    if (!data || !data.columns || data.columns.length === 0) {
      return 1;
    }

    // when child node exists, colSpan is calculated
    return data.columns.reduce((acc, item) => {
      return acc + _checkHeaderColSpan(item);
    }, 0);
  };

  const _childrenRowCount = (data, count = 0) => {
    // when child node does not exists, return count
    if (!data || !data.columns || data.columns.length === 0) {
      return count;
    }

    let cnt = 0;
    // when child node exists, calculate the total number of child node
    const childrenCount = data.columns.reduce((acc, child) => {
      return count - _childrenRowCount(child, cnt + 1);
    }, 0);

    return count - childrenCount;
  };

  // calculate the number of header row count, because there might be merge
  const _checkHeaderRowSpan = (data) => {
    const levels = [];

    const processColumns = (cols, depth = 0) => {
      if (!levels[depth]) {
        levels[depth] = [];
      }

      cols.forEach((col) => {
        levels[depth].push(col);
        if (col.columns) {
          processColumns(col.columns, depth + 1);
        }
      });
    };

    processColumns(data);
    return levels;
  };

  // create `col` tag
  const _createColHeader = (columns) => {
    return columns?.map((item, idx) => {
      if (item.columns?.length > 0) return _createColHeader(item.columns);
      const rowKey = `DataTable+col_${idx}_${Math.random()}`;
      return <col key={`${rowKey}`} style={{ ...(item.width && { width: item.width }) }} />;
    });
  };

  return (
    <div className="tabRightContentsDiv">
      <div className={styles.editTableContainer}>
        <table className={styles.headRow}>
          <colgroup>{_createColHeader(columns)}</colgroup>
          <thead>{_createHeader()}</thead>
          <tbody>
            <DataTableColumn ref={refDataTableColumn} dataSource={currState.transData} columns={currState.columns} />
          </tbody>
        </table>
      </div>
    </div>
  );
});

const DataTablePropTypes = {
  columns: PropTypes.array,
  mergeColumns: PropTypes.array,
  dataSource: PropTypes.array,
  /**
   * className
   */
  className: PropTypes.string,
};

DataTable.propTypes = DataTablePropTypes;
DataTable.defaultProps = {
  columns: [],
  mergeColumns: [],
  dataSource: [],
  className: null,
};
DataTable.displayName = "DataTable";
export default DataTable;
